import { useEffect, useMemo, useState } from "react";
import type { NewsItem } from "@/mocks/news";
import { newsList, categories } from "@/mocks/news";

type Props = {
  activeCat: string;
  onCatChange: (k: string) => void;
  search: string;
  onSearchChange: (s: string) => void;
  onOpen: (a: NewsItem) => void;
};

function catLabel(key: string) {
  const found = categories.find((c) => c.key === key);
  return found ? found.label : key;
}

export default function Feed({ activeCat, onCatChange, search, onSearchChange, onOpen }: Props) {
  const [visible, setVisible] = useState(6);

  const filtered = useMemo(() => {
    const kw = search.replace(/[#\s]/g, "").toLowerCase();
    const list = newsList.filter((n) => {
      if (activeCat !== "all" && n.category !== activeCat) return false;
      if (!kw) return true;
      const hay = `${n.title}${n.summary}${n.content.join("")}${n.tag}${catLabel(n.category)}`.toLowerCase();
      return hay.includes(kw);
    });
    return list;
  }, [activeCat, search]);

  useEffect(() => {
    setVisible(6);
  }, [activeCat, search]);

  const shown = filtered.slice(0, visible);

  return (
    <section id="feed" className="scroll-mt-24 bg-background-50 py-10">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-black text-foreground-950">
              <i className="ri-stack-line text-primary-500"></i> 分类快讯
              <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-sm font-medium text-accent-900">实时更新</span>
            </h2>
            <p className="mt-1.5 text-sm text-foreground-500">今日新鲜快讯聚合，点击分类快速切换，热门事件进展第一时间同步。</p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 md:w-72">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="搜索标题、关键词…"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索快讯"
            />
            {search && (
              <button onClick={() => onSearchChange("")} className="text-foreground-400 hover:text-primary-500 cursor-pointer" aria-label="清空搜索">
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => onCatChange(c.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                activeCat === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {c.label}
            </button>
          ))}
          <span className="ml-auto hidden text-xs text-foreground-400 md:inline">共 {filtered.length} 条快讯</span>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-2xl bg-background-100 p-12 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-base font-medium text-foreground-800">暂时没有匹配“{search}”的快讯</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或回到全部快讯继续浏览。</p>
            <button
              onClick={() => {
                onSearchChange("");
                onCatChange("all");
              }}
              className="mt-4 rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              查看全部快讯
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((n) => (
                <button
                  key={n.id}
                  onClick={() => onOpen(n)}
                  className="group flex flex-col overflow-hidden rounded-xl bg-background-100 text-left hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <div className="relative h-44 w-full overflow-hidden">
                    <img src={n.image} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
                      {catLabel(n.category)}
                    </span>
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-foreground-950/55 px-2 py-0.5 text-[11px] text-background-50 backdrop-blur">
                      <i className="ri-fire-line text-accent-400"></i> {n.views}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <span className="text-[11px] font-medium text-accent-700">{n.tag}</span>
                    <h3 className="mt-1.5 line-clamp-2 text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-600">
                      {n.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{n.summary}</p>
                    <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{n.time}</span>
                      <span className="flex items-center gap-1 font-medium text-primary-600">阅读全文 <i className="ri-arrow-right-line"></i></span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setVisible((v) => v + 6)}
                  className="rounded-full border border-background-200 bg-background-100 px-8 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  加载更多快讯
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}